import React from 'react';
import { Box, Container, Typography, Paper } from '@mui/material';
import { useDarkMode, getClozedStyles } from '../../hooks/useDarkMode';

interface TermsTemplateProps {
  title: string;
  content: string;
  lastUpdated?: string;
}

export default function TermsTemplate({ title, content, lastUpdated }: TermsTemplateProps) {
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: isDarkMode ? '#121212' : '#f5f5f5',
      py: 4
    }}>
      <Container maxWidth="md">
        <Paper sx={{
          ...styles.paper,
          p: { xs: 3, md: 5 },
          borderRadius: 2
        }}>
          {/* 헤더 */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography
              variant="h3"
              fontWeight={700}
              sx={{
                color: styles.text.primary,
                mb: 1,
                fontSize: { xs: '1.75rem', md: '2.5rem' }
              }}
            >
              {title}
            </Typography>
            {lastUpdated && (
              <Typography
                variant="body2"
                sx={{ color: styles.text.secondary }}
              >
                최종 수정일: {lastUpdated}
              </Typography>
            )}
          </Box>

          {/* 내용 */}
          <Box sx={{
            color: styles.text.primary,
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              color: styles.text.primary,
              fontWeight: 700,
              mt: 3,
              mb: 2
            },
            '& h1': { fontSize: '1.75rem' },
            '& h2': { fontSize: '1.5rem' },
            '& h3': { fontSize: '1.25rem' },
            '& p': {
              color: styles.text.primary,
              lineHeight: 1.8,
              mb: 2
            },
            '& ul, & ol': {
              color: styles.text.primary,
              pl: 3,
              mb: 2
            },
            '& li': {
              mb: 1,
              lineHeight: 1.8
            },
            '& strong': {
              fontWeight: 700,
              color: styles.text.primary
            },
            '& a': {
              color: '#1976d2',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline'
              }
            },
            '& table': {
              width: '100%',
              borderCollapse: 'collapse',
              mb: 2,
              '& th, & td': {
                border: `1px solid ${isDarkMode ? '#404040' : '#e0e0e0'}`,
                padding: '12px',
                textAlign: 'left'
              },
              '& th': {
                backgroundColor: isDarkMode ? '#2a2a2a' : '#f5f5f5',
                fontWeight: 700
              }
            }
          }}>
            <div dangerouslySetInnerHTML={{ __html: content }} />
          </Box>

          {/* 푸터 */}
          <Box sx={{
            mt: 5,
            pt: 3,
            borderTop: `1px solid ${isDarkMode ? '#404040' : '#e0e0e0'}`,
            textAlign: 'center'
          }}>
            <Typography
              variant="body2"
              sx={{ color: styles.text.secondary }}
            >
              본 약관은 2025년 1월 1일부터 시행됩니다.
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: styles.text.secondary, mt: 1 }}
            >
              문의: admin@clozet.my
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
